import type { ContentItemPlatform } from "@shared/schema";
import type { PublicGalleryItem } from "@shared/content-gallery";
import { CONTENT_TOPIC_LABELS } from "@shared/content-topics";
import { BRAND } from "@/lib/brand-config";

const PLATFORM_LABELS: Record<ContentItemPlatform, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube_long: "YouTube",
  youtube_shorts: "YouTube Shorts",
  linkedin: "LinkedIn",
  x: "X",
  facebook: "Facebook",
};

const PLATFORM_FOLLOW: Partial<Record<ContentItemPlatform, string>> = {
  linkedin: BRAND.social.linkedin,
  youtube_long: BRAND.social.youtube,
  youtube_shorts: BRAND.social.youtube,
  instagram: BRAND.social.instagram,
  facebook: BRAND.social.facebook,
  tiktok: BRAND.social.tiktok,
  x: BRAND.social.x || undefined,
};

function formatPublished(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function GalleryMedia({
  item,
  testIdPrefix,
}: {
  item: PublicGalleryItem;
  testIdPrefix: string;
}) {
  if (item.thumbnailUrl) {
    return (
      <img
        src={item.thumbnailUrl}
        alt=""
        className="h-full w-full object-cover transition-[transform,filter] duration-700 ease-out group-hover:scale-[1.02] group-hover:brightness-[1.03]"
        loading="lazy"
        decoding="async"
        data-testid={`${testIdPrefix}-thumb-${item.id}`}
      />
    );
  }

  // Quiet placeholder — no platform badges or abbreviation chips.
  return (
    <div
      className="relative flex h-full w-full items-end bg-[#0A2540]"
      aria-hidden="true"
    >
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(145deg, rgba(201,168,76,0.18) 0%, transparent 42%), linear-gradient(to top, rgba(6,24,40,0.9), transparent 55%)",
        }}
      />
      <span className="relative z-10 px-4 pb-3 text-[11px] tracking-[0.14em] uppercase text-white/55">
        {PLATFORM_LABELS[item.primaryPlatform]}
      </span>
    </div>
  );
}

export function ContentGalleryGrid({
  items,
  testIdPrefix = "content-gallery",
  compact = false,
}: {
  items: PublicGalleryItem[];
  testIdPrefix?: string;
  /** Tighter homepage teaser: fewer columns of chrome. */
  compact?: boolean;
}) {
  if (items.length === 0) return null;

  return (
    <ul
      className={`grid grid-cols-1 sm:grid-cols-2 ${compact ? "lg:grid-cols-3 gap-x-8 gap-y-10" : "lg:grid-cols-3 gap-x-10 gap-y-12"} list-none p-0 m-0`}
      data-testid={`${testIdPrefix}-grid`}
    >
      {items.map((item) => {
        const when = formatPublished(item.publishedAt);
        const meta = [PLATFORM_LABELS[item.primaryPlatform], when]
          .filter(Boolean)
          .join(" · ");

        return (
          <li key={item.id} className="min-w-0">
            <a
              href={item.primaryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/70 focus-visible:ring-offset-4"
              data-testid={`${testIdPrefix}-card-${item.id}`}
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-[#0A2540]">
                <GalleryMedia item={item} testIdPrefix={testIdPrefix} />
              </div>
              <div className={compact ? "pt-4" : "pt-5"}>
                <p
                  className="text-[11px] tracking-[0.12em] uppercase text-muted-foreground mb-2"
                  data-testid={`${testIdPrefix}-meta-${item.id}`}
                >
                  {meta}
                </p>
                <h3
                  className="font-heading text-lg leading-snug text-[#0A2540] transition-colors duration-300 group-hover:text-[#0A2540]/80"
                  data-testid={`${testIdPrefix}-title-${item.id}`}
                >
                  <span className="bg-[linear-gradient(currentColor,currentColor)] bg-left-bottom bg-no-repeat bg-[length:0%_1px] transition-[background-size] duration-300 group-hover:bg-[length:100%_1px]">
                    {item.title}
                  </span>
                </h3>
                {item.spokesperson && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {item.spokesperson}
                  </p>
                )}
                {(item.topics?.length ?? 0) > 0 && (
                  <p
                    className="mt-2 text-xs text-muted-foreground"
                    data-testid={`${testIdPrefix}-topics-${item.id}`}
                  >
                    {item.topics
                      .map((topic) => CONTENT_TOPIC_LABELS[topic] ?? topic)
                      .join(" · ")}
                  </p>
                )}
              </div>
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/** Quiet text links — not pill chips. */
export function ContentGalleryFollowRow({
  className = "",
}: {
  className?: string;
}) {
  const links = (
    Object.entries(PLATFORM_FOLLOW) as [ContentItemPlatform, string][]
  ).filter(([, url]) => Boolean(url));

  if (links.length === 0) return null;

  return (
    <nav
      className={`flex flex-wrap items-center justify-center gap-x-1 gap-y-2 text-sm text-muted-foreground ${className}`}
      aria-label="Follow Veras on social"
      data-testid="content-gallery-follow"
    >
      <span className="mr-2 text-[11px] tracking-[0.14em] uppercase">
        Follow
      </span>
      {links.map(([platform, url], index) => (
        <span key={platform} className="inline-flex items-center">
          {index > 0 && (
            <span className="mx-2 text-border" aria-hidden="true">
              ·
            </span>
          )}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-[#0A2540]"
            data-testid={`link-follow-${platform}`}
          >
            {PLATFORM_LABELS[platform]}
          </a>
        </span>
      ))}
    </nav>
  );
}
